import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

const LocationContext = createContext(null);

export const ADMIN_SECTIONS = ['overview', 'users', 'plans', 'subscriptions', 'transactions'];

export function isAdminPath(pathname) {
  return pathname === '/admin' || pathname.startsWith('/admin/');
}

export function sectionFromPath(pathname) {
  if (pathname === '/admin' || pathname === '/admin/') return 'overview';
  if (!pathname.startsWith('/admin/')) return null;
  const rest = pathname.slice('/admin/'.length).replace(/\/$/, '');
  if (ADMIN_SECTIONS.includes(rest)) return rest;
  return 'unknown';
}

export function pathForSection(section) {
  return section === 'overview' ? '/admin' : `/admin/${section}`;
}

function isPlainClick(event) {
  return event.button === 0
    && !event.metaKey
    && !event.ctrlKey
    && !event.shiftKey
    && !event.altKey;
}

export function followLink(event, navigate, path) {
  if (!isPlainClick(event)) return;
  event.preventDefault();
  navigate(path);
}

export function LocationProvider({ children }) {
  const [pathname, setPathname] = useState(() => window.location.pathname);

  useEffect(() => {
    const sync = () => setPathname(window.location.pathname);
    window.addEventListener('popstate', sync);
    return () => window.removeEventListener('popstate', sync);
  }, []);

  const navigate = useCallback((path, { replace = false } = {}) => {
    const next = path.startsWith('/') ? path : `/${path}`;
    if (window.location.pathname === next) return;
    window.history[replace ? 'replaceState' : 'pushState'](null, '', next);
    setPathname(next);
  }, []);

  const value = useMemo(() => ({ pathname, navigate }), [pathname, navigate]);
  return <LocationContext.Provider value={value}>{children}</LocationContext.Provider>;
}

export function useLocation() {
  const value = useContext(LocationContext);
  if (!value) throw new Error('useLocation must be used within LocationProvider');
  return value;
}
